import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  DatePicker,
  Flashbar,
  Header,
  Link,
  Multiselect,
  Pagination,
  Popover,
  Select,
  SpaceBetween,
  Table,
} from "@amzn/awsui-components-react";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { useRecentTransactions } from "../hooks/useRecentTransactions";
import {
  COLUMN_DEFINITIONS,
  CONSUMPTION_TYPE_OPTIONS,
  DEFAULT_OPTION,
  FEATURES_OPTIONS,
  getModifiedOptions,
  GLS,
  GROUPED_MARKETPLACES,
  GROUPED_USECASES,
  PAYMENT_METHOD_OPTIONS,
  RECENT_TRXN_EMPTY_STATE,
  STAGES,
  TABLE_PREFERENCES,
  TIME_RANGES,
} from "../config/recentTransactionTableConfig";
import ViewLogs from "./common/ViewLogs";
import { publisherLogGroups } from "../helpers/logUtils";
import { EmptyState } from "./common/EmptyState";
import { Transaction } from "../lusca-search-api/generated-src";
import { SearchFilters } from "../types/common";
import { useFolderUI } from "../hooks/useFolderUI";
import useFolders from "../hooks/useFolders";
import { FolderModal } from "./folders/FolderModal";
import { getGlMapping } from "../hooks/useSummaryData";

interface RecentTransactionsProps {
  isRaven?: boolean;
  selectedOrders?: Transaction[];
  setSelectedOrders?: (orders: Transaction[]) => void;
}

export default function RecentTransactions(props: RecentTransactionsProps) {
  const [selectedTransactions, setSelectedTransactions] = useState<
    Transaction[]
  >([]);
  const { folders, addTransactions, saveFolder } = useFolders();
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI();

  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    handleSearch,
    isLoading,
    selectedFilters,
    setSelectedFilters,
    clearFilter,
    executedQuery,
    error: searchError,
  } = useRecentTransactions();

  const modifiedMarketplaces = useMemo(
    () =>
      getModifiedOptions(GROUPED_MARKETPLACES, selectedFilters.marketplaces),
    [selectedFilters.marketplaces]
  );

  const modifiedUsecases = useMemo(
    () => getModifiedOptions(GROUPED_USECASES, selectedFilters.usecases),
    [selectedFilters.usecases]
  );

  const modifiedConsumptionType = useMemo(
    () =>
      getModifiedOptions(
        CONSUMPTION_TYPE_OPTIONS,
        selectedFilters.consumptionTypes
      ),
    [selectedFilters.consumptionTypes]
  );

  const handleMultiselectChange =
    <K extends keyof SearchFilters>(filter: K) =>
    ({
      detail,
    }: {
      detail: { selectedOptions: readonly OptionDefinition[] };
    }) => {
      let newSelection = [...detail.selectedOptions] as OptionDefinition[];
      const currentSelection = selectedFilters[filter] as OptionDefinition[];
      const hadAll = currentSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );
      const hasAll = newSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );

      if (hasAll && !hadAll) {
        newSelection = [DEFAULT_OPTION];
      } else if (hadAll) {
        newSelection = [];
      }
      setSelectedFilters({
        ...selectedFilters,
        [filter]: newSelection,
      });
    };

  const handleAddToFolder = () => {
    if (selectedTransactions.length === 0) {
      return;
    }
    setIsModalVisible(true);
  };

  const handleFolderSelect = () => {
    if (selectedFolderId && selectedTransactions.length > 0) {
      const result = addTransactions(
        selectedFolderId,
        selectedTransactions.map((transaction) => ({
          date: new Date().toISOString(),
          id: transaction.id || "",
          stage: selectedFilters.stage.value || "",
          status: "unknown",
          country: transaction.country,
          gl: transaction.gl ? getGlMapping(transaction.gl) : "",
          usecase: transaction.usecase || "",
          paymentMethod: transaction.paymentMethod || "",
          labels: [],
          consumptionTypes: [],
        }))
      );

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });

      if (result.success) {
        setSelectedTransactions([]);
        handleModalDismiss();
      }
    }
  };

  const tableFooter = useMemo(() => {
    if (isLoading || items.length >= 15 || selectedFilters.deep) {
      return null;
    }

    return (
      <Box textAlign="center">
        {items.length === 0
          ? "No results found. "
          : "Limited results found in default search window. "}
        <Link
          onFollow={() => {
            setSelectedFilters((f) => ({ ...f, deep: true }));
            handleSearch({ deep: true });
          }}
        >
          Search deeper?
        </Link>{" "}
      </Box>
    );
  }, [items.length, selectedFilters.deep, isLoading]);

  const loadingText = useMemo(() => {
    if (!isLoading) return "";
    return selectedFilters.deep
      ? "Performing deep search (this may take longer)..."
      : "Searching for transactions";
  }, [isLoading, selectedFilters.deep]);

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Table
        {...collectionProps}
        {...TABLE_PREFERENCES}
        resizableColumns
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        loading={isLoading}
        loadingText={loadingText}
        selectionType="multi"
        onSelectionChange={({ detail }) => {
          if (props.isRaven && props.setSelectedOrders) {
            props.setSelectedOrders(detail.selectedItems);
          } else if (!props.isRaven) {
            setSelectedTransactions(detail.selectedItems);
          }
        }}
        selectedItems={
          props.isRaven ? props.selectedOrders : selectedTransactions
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ViewLogs
                  stage={selectedFilters.stage.value!}
                  logGroups={publisherLogGroups(
                    selectedFilters.stage.value!
                  )}
                  logQuery={executedQuery!}
                  minimal
                />
                {!props.isRaven && (
                  <Button
                    onClick={handleAddToFolder}
                    disabled={selectedTransactions.length === 0}
                  >
                    Add to Folder
                  </Button>
                )}
              </SpaceBetween>
            }
          >
            Find Transactions{" "}
            <Popover
              dismissButton={false}
              fixedWidth={false}
              position="bottom"
              triggerType="custom"
              content="Searches look at the last 2 weeks of logs from your specified start time. To choose a specific start time range, or add custom filters, open in CloudWatch"
            >
              <Button iconName="status-info" variant="inline-icon" />
            </Popover>
          </Header>
        }
        filter={
          <div className="input-container">
            <div className="select-filter" style={{ maxInlineSize: "110px" }}>
              <Select
                data-testid="stage-filter"
                inlineLabelText="Stage"
                options={STAGES}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.stage}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    stage: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "150px" }}>
              <Select
                inlineLabelText="GL"
                data-testid="gl-filter"
                options={GLS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.gl}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    gl: detail.selectedOption,
                  })
                }
                expandToViewport
                filteringType="auto"
                filteringPlaceholder="Find GL"
                empty="No matching GLs"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "150px" }}>
              <Select
                inlineLabelText="Payment Type"
                data-testid="payment-method-filter"
                options={PAYMENT_METHOD_OPTIONS}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.paymentMethod}
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    paymentMethod: detail.selectedOption,
                  })
                }
                expandToViewport
              />
            </div>
            <div className="select-filter">
              <Multiselect
                placeholder="Marketplaces"
                inlineTokens
                data-testid="marketplace-filter"
                options={modifiedMarketplaces}
                selectedOptions={selectedFilters.marketplaces}
                onChange={handleMultiselectChange("marketplaces")}
                expandToViewport
                filteringType="auto"
                filteringPlaceholder="Find marketplaces"
                empty="No matching marketplaces"
              />
            </div>
            <div className="select-filter">
              <Multiselect
                placeholder="Usecases"
                data-testid="usecase-filter"
                inlineTokens
                options={modifiedUsecases}
                selectedOptions={selectedFilters.usecases}
                onChange={handleMultiselectChange("usecases")}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find usecases"
                empty="No matching usecases"
              />
            </div>
            <div className="select-filter">
              <Multiselect
                placeholder="Consumption Type"
                data-testid="consumption-type-filter"
                inlineTokens
                options={modifiedConsumptionType}
                selectedOptions={selectedFilters.consumptionTypes}
                onChange={handleMultiselectChange("consumptionTypes")}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find Consumption Type"
                empty="No matching consumption type"
              />
            </div>
            <div className="select-filter">
              <Multiselect
                placeholder="Features"
                data-testid="features-filter"
                inlineTokens
                options={FEATURES_OPTIONS}
                selectedOptions={selectedFilters.features}
                onChange={handleMultiselectChange("features")}
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find Features"
                empty="No matching features"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "150px" }}>
              <Select
                inlineLabelText="Time Range"
                data-testid="time-range-filter"
                options={TIME_RANGES}
                selectedAriaLabel="Selected"
                selectedOption={selectedFilters.timeRange}
                onChange={({ detail }) => {
                  setSelectedFilters({
                    ...selectedFilters,
                    timeRange: detail.selectedOption,
                    date:
                      detail.selectedOption.value !== "custom"
                        ? ""
                        : selectedFilters.date,
                  });
                }}
                expandToViewport
              />
            </div>
            {selectedFilters.timeRange.value === "custom" && (
              <div className="select-filter" style={{ maxInlineSize: "200px" }}>
                <DatePicker
                  onChange={({ detail }) =>
                    setSelectedFilters({
                      ...selectedFilters,
                      date: detail.value,
                    })
                  }
                  value={selectedFilters.date}
                  placeholder="Select a date"
                  previousMonthAriaLabel="Previous month"
                  nextMonthAriaLabel="Next month"
                  todayAriaLabel="Today"
                  expandToViewport
                  isDateEnabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return new Date(date) <= today;
                  }}
                />
              </div>
            )}

            <Button variant="primary" onClick={() => handleSearch()}>
              Search
            </Button>
            <Button onClick={clearFilter}>Clear filters</Button>
          </div>
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <EmptyState
            error={searchError}
            filteringText={filterProps.filteringText}
            config={RECENT_TRXN_EMPTY_STATE}
            action={
              !selectedFilters.deep &&
              !filterProps.filteringText &&
              !searchError
                ? {
                    text: "Search deeper?",
                    onClick: () => handleSearch({ deep: true }),
                  }
                : undefined
            }
          />
        }
        footer={items.length > 0 && !isLoading ? tableFooter : null}
      />

      {!props.isRaven && (
        <FolderModal
          isModalVisible={isModalVisible}
          isFolderModalVisible={isFolderModalVisible}
          isShareModalVisible={false}
          selectedFolderId={selectedFolderId}
          folderName={folderName}
          error={error}
          folders={folders}
          sharingFolder={null}
          onModalDismiss={handleModalDismiss}
          onCreateFolder={handleCreateFolder}
          onFolderSelect={handleFolderSelect}
          onFolderModalDismiss={handleFolderModalDismiss}
          onShareModalDismiss={() => {}}
          onSaveFolder={() => handleSaveFolder(saveFolder)}
          setSelectedFolderId={setSelectedFolderId}
          setFolderName={setFolderName}
        />
      )}
    </>
  );
}
